// "Heute", the home of the app: what is due, ordered by date, from the synthetic case. Every task carries its
// date, its consequence and one action that opens the comparison or the contract file it belongs to. The sample
// tag and the source line keep the case honest: these are invented figures, not the user's own data.

import { Glyph } from "../../components/Glyph";
import { daysBetween, formatDay, formatLong } from "../../lib/format";
import { useT, type MessageId } from "../../lib/i18n";
import { MOCK_TODAY, type HeuteTask, type TaskAction } from "../../lib/mock";
import { mockTasks } from "../../lib/mock-heute";
import { CaseLink } from "./CaseLink";
import { usePageTitle } from "./usePageTitle";

const actionMessage = {
  deadline: "task.action.deadline",
  answer: "task.action.answer",
  letter: "task.action.letter",
} as const satisfies Record<TaskAction, MessageId>;

function LeftLabel({ task }: { task: HeuteTask }) {
  const t = useT();
  const days = daysBetween(MOCK_TODAY, task.dateISO);
  if (days === 0) return <span className="sf-task__left">{t("task.left.today")}</span>;
  if (days > 0) return <span className="sf-task__left">{t("task.left.days", { count: days })}</span>;
  return <span className="sf-task__left">{t("task.left.overdue", { count: -days })}</span>;
}

export function Heute() {
  const t = useT();
  usePageTitle(t("heute.title"));
  return (
    <>
      <div className="sf-pagehead">
        <h1 className="sf-pagehead__title">{t("heute.title")}</h1>
        <p className="sf-pagehead__meta">
          {t("heute.meta", { date: formatLong(MOCK_TODAY), count: mockTasks.length })}{" "}
          <span className="sf-tag sf-tag--sample">{t("sample.tag")}</span>
        </p>
      </div>
      <div className="sf-block">
        <ol className="sf-tasks" aria-label={t("heute.title")}>
          {mockTasks.map((task, index) => (
            <li className="sf-task" key={task.id}>
              <p className="sf-task__date">
                <span className="sf-task__day">{formatDay(task.dateISO)}</span>
                <LeftLabel task={task} />
              </p>
              <div className="sf-task__body">
                <h2 className="sf-task__title">
                  <Glyph name={task.glyph} />
                  {task.title}
                </h2>
                <p className="sf-task__meta">{task.meta}</p>
                <p className="sf-task__then">
                  {task.then.map((segment, at) =>
                    segment.strong ? <strong key={at}>{segment.text}</strong> : <span key={at}>{segment.text}</span>,
                  )}
                </p>
              </div>
              <CaseLink
                target={task.target}
                className={index === 0 ? "sf-plate sf-plate--primary sf-task__act" : "sf-plate sf-plate--secondary sf-task__act"}
              >
                {t(actionMessage[task.action])}
              </CaseLink>
            </li>
          ))}
        </ol>
      </div>
    </>
  );
}
